/**
 * /customer/profile — identity + favorites + sign out (PHASE W-1).
 *
 * Minimal-by-design (operator note from W-1 dispatch):
 *   - Customer name + Membership ID + email + phone (read-only).
 *   - Pinned/favorite brands list with unpin action.
 *   - Sign out button — clears Zustand state + bounces to /customer/login.
 *
 * No settings, no preferences, no profile edits. Identity surface only.
 */
import { useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  LogOut, User, Mail, Phone, Hash, Star, Loader2, X,
  Clock, Ticket, Bell, HelpCircle, FileText, ChevronRight, Settings, Crown,
} from 'lucide-react';
import { toast } from 'sonner';
import { api, resolveImageUrl } from '@/lib/api';
import { qk } from '@/lib/queryKeys';
import { useAuthStore } from '@/lib/auth';
import TierBadge from '@/components/membership/TierBadge';
import { rupiah, formatDate, expiryLabel, STATUS_PILL } from '@/lib/membershipFmt';
import { StatusPill } from '@/components/ui_primitives/Misc';

export default function CustomerProfilePage() {
  const navigate = useNavigate();
  const token = useAuthStore((s) => s.token);
  const user = useAuthStore((s) => s.user);
  const signOut = useAuthStore((s) => s.signOut);
  const queryClient = useQueryClient();

  const favoritesQ = useQuery({
    queryKey: qk.customerFavorites(),
    queryFn: () => api.customerFavorites(token),
    enabled: !!token,
  });

  const membershipsQ = useQuery({
    queryKey: qk.customerMyMemberships(),
    queryFn: () => api.customerMyMemberships(token),
    enabled: !!token,
  });

  const unpinMut = useMutation({
    mutationFn: (brandId) => api.customerRemoveFavorite(token, brandId),
    onSuccess: (_d, brandId) => {
      toast.success('Unpinned');
      // Optimistic: drop locally + refetch
      queryClient.setQueryData(qk.customerFavorites(), (old) =>
        (old ?? []).filter((b) => b.id !== brandId),
      );
      queryClient.invalidateQueries({ queryKey: qk.customerFavorites() });
    },
    onError: (e) => toast.error(e?.detail ?? 'Could not unpin brand'),
  });

  const onSignOut = () => {
    signOut();
    navigate('/customer/login', { replace: true });
  };

  const initial = (user?.name ?? user?.email ?? '?').slice(0, 1).toUpperCase();

  return (
    <div className="space-y-8" data-testid="customer-profile-screen">
      {/* Identity hero */}
      <section>
        <div className="rounded-3xl border border-white/8 bg-[#15151B] p-6 md:p-7 flex items-center gap-5">
          <div
            className="w-16 h-16 rounded-3xl bg-gold/15 border border-gold/30 grid place-items-center text-gold text-2xl font-display font-semibold shrink-0"
            data-testid="customer-profile-avatar"
          >
            {initial}
          </div>
          <div className="min-w-0">
            <h1
              className="font-display text-2xl font-bold truncate"
              data-testid="customer-profile-name"
            >
              {user?.name ?? '—'}
            </h1>
            <div
              className="text-xs font-mono text-gold mt-1 flex items-center gap-1.5"
              data-testid="customer-profile-membership-id"
            >
              <Hash size={11} /> {user?.membership_id ?? '—'}
            </div>
          </div>
        </div>
      </section>

      {/* Contact details (read-only) */}
      <section data-testid="customer-profile-details">
        <h2 className="font-display text-lg font-semibold mb-3">Account</h2>
        <div className="rounded-2xl border border-white/8 bg-[#15151B] divide-y divide-white/5">
          <Row
            icon={<User size={14} />}
            label="Name"
            value={user?.name ?? '—'}
            testID="customer-profile-row-name"
          />
          <Row
            icon={<Mail size={14} />}
            label="Email"
            value={user?.email ?? '—'}
            testID="customer-profile-row-email"
          />
          <Row
            icon={<Phone size={14} />}
            label="Phone"
            value={user?.phone || 'Not provided'}
            testID="customer-profile-row-phone"
          />
        </div>
      </section>

      {/* Favorites */}
      <section data-testid="customer-profile-favorites">
        <div className="flex items-end justify-between mb-3">
          <h2 className="font-display text-lg font-semibold">Pinned brands</h2>
          <Star size={14} className="text-gold" />
        </div>
        {favoritesQ.isLoading ? (
          <div className="h-20 rounded-2xl bg-white/[0.03] border border-white/5 animate-pulse" />
        ) : (favoritesQ.data ?? []).length === 0 ? (
          <div
            className="rounded-2xl border border-white/8 bg-[#15151B] p-6 text-center"
            data-testid="customer-profile-favorites-empty"
          >
            <Star size={22} className="mx-auto text-gold/50 mb-2" />
            <div className="text-sm font-medium">No pinned brands yet</div>
            <div className="text-xs text-white/45 mt-1">
              Pin your favourites from a brand page to keep them handy.
            </div>
          </div>
        ) : (
          <div className="rounded-2xl border border-white/8 bg-[#15151B] divide-y divide-white/5">
            {(favoritesQ.data ?? []).map((b) => (
              <div
                key={b.id}
                data-testid={`customer-profile-fav-${b.id}`}
                className="flex items-center gap-3 px-4 py-3"
              >
                <button
                  onClick={() => navigate(`/customer/brand/${b.id}`)}
                  className="flex items-center gap-3 min-w-0 flex-1 text-left"
                >
                  {b.logo_url ? (
                    <img
                      src={resolveImageUrl(b.logo_url)}
                      alt=""
                      className="w-10 h-10 rounded-xl border border-white/10 shrink-0"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-xl bg-gold/15 border border-gold/30 grid place-items-center text-gold font-semibold shrink-0">
                      {(b.name ?? '?').slice(0, 1)}
                    </div>
                  )}
                  <div className="min-w-0">
                    <div className="font-medium text-sm truncate">{b.name}</div>
                    <div className="text-xs text-white/45 truncate">
                      {b.branch_count ?? 0} location
                      {(b.branch_count ?? 0) === 1 ? '' : 's'}
                    </div>
                  </div>
                </button>
                <button
                  onClick={() => unpinMut.mutate(b.id)}
                  disabled={unpinMut.isPending}
                  data-testid={`customer-profile-unpin-${b.id}`}
                  aria-label={`Unpin ${b.name}`}
                  className="w-8 h-8 grid place-items-center rounded-lg border border-white/10 bg-white/5 hover:bg-rose-400/10 hover:border-rose-400/30 hover:text-rose-300 text-white/55 transition-colors disabled:opacity-40"
                >
                  {unpinMut.isPending && unpinMut.variables === b.id ? (
                    <Loader2 size={13} className="animate-spin" />
                  ) : (
                    <X size={13} />
                  )}
                </button>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* OneStamp Pro Phase 1 — Memberships section.
          Active memberships are surfaced as full-bleed cards; expired /
          cancelled rows fold into a dim history collapse below. Hidden
          entirely when the customer has zero memberships of any state. */}
      <MembershipsSection q={membershipsQ} />

      {/* Quick Access — Phase 2d. Mirror of mobile profile.tsx lines
          211–304 (Birthday rewards row deliberately omitted per
          operator §11 — deferred to a separate dispatch). */}
      <section
        className="rounded-3xl border border-gold/25 bg-[#15151B] p-1"
        data-testid="customer-profile-quick-access-card"
      >
        <div className="px-4 pt-4 pb-2 text-base font-semibold">
          Quick Access
        </div>
        <QuickRow
          to="/customer/history"
          icon={<Clock size={16} />}
          label="Activity History"
          testID="customer-profile-quick-access-history"
        />
        <DividerLine />
        <QuickRow
          to="/customer/redemptions"
          icon={<Ticket size={16} />}
          label="My Redemptions"
          testID="customer-profile-quick-access-redemptions"
        />
        <DividerLine />
        <QuickRow
          to="/customer/settings"
          icon={<Settings size={16} />}
          label="Settings"
          testID="customer-profile-quick-access-settings"
        />
        <DividerLine />
        <QuickRow
          to="/customer/notifications"
          icon={<Bell size={16} />}
          label="Notifications"
          testID="customer-profile-quick-access-notifications"
        />
        <DividerLine />
        <QuickRow
          to="/customer/help"
          icon={<HelpCircle size={16} />}
          label="Help"
          testID="customer-profile-quick-access-help"
        />
        <DividerLine />
        <QuickRow
          to="/customer/terms"
          icon={<FileText size={16} />}
          label="Terms of Service"
          testID="customer-profile-quick-access-terms"
        />
      </section>

      {/* Sign out */}
      <section>
        <button
          type="button"
          onClick={onSignOut}
          data-testid="customer-profile-signout"
          className="w-full inline-flex items-center justify-center gap-2 rounded-2xl border border-rose-400/25 bg-rose-500/10 hover:bg-rose-500/15 text-rose-200 font-medium py-3 text-sm transition-colors"
        >
          <LogOut size={14} /> Sign out
        </button>
        <div className="text-center text-[11px] text-white/35 mt-3">
          OneStamp
        </div>
      </section>
    </div>
  );
}

function Row({ icon, label, value, testID }) {
  return (
    <div
      className="flex items-center gap-3 px-4 py-3.5"
      data-testid={testID}
    >
      <div className="w-8 h-8 rounded-lg bg-white/5 border border-white/8 grid place-items-center text-white/55 shrink-0">
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-wider text-white/40 font-mono">
          {label}
        </div>
        <div className="text-sm truncate mt-0.5">{value}</div>
      </div>
    </div>
  );
}


function QuickRow({ to, icon, label, testID }) {
  return (
    <Link
      to={to}
      data-testid={testID}
      className="flex items-center gap-3 px-4 py-3.5 group hover:bg-gold/[0.04] transition-colors rounded-2xl"
    >
      <div className="w-8 h-8 rounded-lg bg-gold/15 border border-gold/30 grid place-items-center text-gold shrink-0">
        {icon}
      </div>
      <div className="flex-1 text-sm text-white/90 group-hover:text-white">
        {label}
      </div>
      <ChevronRight
        size={16}
        className="text-white/45 group-hover:text-gold transition-colors"
      />
    </Link>
  );
}

function DividerLine() {
  return <div className="h-px bg-white/6 mx-4" aria-hidden="true" />;
}

// ---------------------------------------------------------------------------
// OneStamp Pro Phase 1 — Memberships section
// ---------------------------------------------------------------------------
function MembershipsSection({ q }) {
  if (q.isLoading) {
    return (
      <section data-testid="customer-profile-memberships-loading">
        <h2 className="font-display text-lg font-semibold mb-3 inline-flex items-center gap-2">
          <Crown size={16} className="text-gold" />
          Memberships
        </h2>
        <div className="h-24 rounded-2xl bg-white/[0.03] border border-white/5 animate-pulse" />
      </section>
    );
  }
  const all = q.data ?? [];
  // Hidden-if-zero rule: nothing is rendered if the customer has no
  // membership rows of any state. (Spec §3 / acceptance item 4.)
  if (all.length === 0) return null;

  const active = all.filter((m) => m.effective_status === 'active');
  const history = all.filter((m) => m.effective_status !== 'active');

  return (
    <section data-testid="customer-profile-memberships">
      <div className="flex items-end justify-between mb-3">
        <h2 className="font-display text-lg font-semibold inline-flex items-center gap-2">
          <Crown size={16} className="text-gold" />
          Memberships
        </h2>
        <span className="text-[11px] uppercase tracking-wider text-white/40 font-mono">
          {active.length} active
        </span>
      </div>

      {active.length === 0 ? (
        <div
          className="rounded-2xl border border-white/8 bg-[#15151B] p-6 text-center"
          data-testid="customer-profile-memberships-empty-active"
        >
          <Crown size={22} className="mx-auto text-gold/50 mb-2" />
          <div className="text-sm font-medium">No active memberships</div>
          <div className="text-xs text-white/45 mt-1">
            Your past memberships are listed below.
          </div>
        </div>
      ) : (
        <div className="space-y-3" data-testid="customer-profile-memberships-active">
          {active.map((m) => (
            <ActiveMembershipCard key={m.id} m={m} />
          ))}
        </div>
      )}

      {history.length > 0 ? (
        <div className="mt-4" data-testid="customer-profile-memberships-history">
          <div className="text-[11px] uppercase tracking-wider text-white/40 font-mono mb-2">
            History
          </div>
          <div className="rounded-2xl border border-white/8 bg-[#15151B]/60 divide-y divide-white/5">
            {history.map((m) => {
              const sp = STATUS_PILL[m.effective_status] || STATUS_PILL.cancelled;
              return (
                <div
                  key={m.id}
                  data-testid={`customer-profile-membership-history-${m.id}`}
                  className="flex items-center gap-3 px-4 py-3 opacity-75"
                >
                  {m.brand_logo_url ? (
                    <img
                      src={resolveImageUrl(m.brand_logo_url)}
                      alt=""
                      className="w-9 h-9 rounded-xl border border-white/10 shrink-0 grayscale"
                    />
                  ) : (
                    <div className="w-9 h-9 rounded-xl bg-white/5 border border-white/10 grid place-items-center text-white/60 font-semibold shrink-0">
                      {(m.brand_name ?? '?').slice(0, 1)}
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate text-white/80">
                      {m.brand_name} · {m.plan?.name ?? '—'}
                    </div>
                    <div className="text-[11px] text-white/45 truncate mt-0.5">
                      {expiryLabel(m)}
                    </div>
                  </div>
                  <StatusPill variant={sp.variant}>{sp.label}</StatusPill>
                </div>
              );
            })}
          </div>
        </div>
      ) : null}
    </section>
  );
}

function ActiveMembershipCard({ m }) {
  const tierColor = m.tier?.display_color || '#C9A84C';
  const sp = STATUS_PILL[m.effective_status] || STATUS_PILL.active;
  return (
    <div
      data-testid={`customer-profile-membership-${m.id}`}
      className="rounded-3xl border bg-[#15151B] p-5 relative overflow-hidden"
      style={{
        borderColor: `${tierColor}55`,
        background: `linear-gradient(135deg, ${tierColor}11, #15151B 55%)`,
      }}
    >
      <div
        className="absolute -right-12 -top-12 w-40 h-40 rounded-full opacity-25 blur-3xl"
        style={{ background: tierColor }}
        aria-hidden="true"
      />
      <div className="flex items-center gap-3 mb-3 relative">
        {m.brand_logo_url ? (
          <img
            src={resolveImageUrl(m.brand_logo_url)}
            alt=""
            className="w-12 h-12 rounded-2xl border border-white/15 shadow-xl bg-black/40 shrink-0"
          />
        ) : (
          <div
            className="w-12 h-12 rounded-2xl border grid place-items-center font-bold shrink-0"
            style={{ borderColor: `${tierColor}55`, background: `${tierColor}22`, color: tierColor }}
          >
            {(m.brand_name ?? '?').slice(0, 1)}
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="text-base font-semibold truncate">{m.brand_name}</div>
          <div className="text-xs text-white/60 truncate mt-0.5">
            {m.plan?.name ?? 'Membership'}
          </div>
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          <StatusPill variant={sp.variant}>{sp.label}</StatusPill>
          {!m.expiry_date ? <StatusPill variant="gold">Lifetime</StatusPill> : null}
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 mt-3 relative">
        {m.tier ? <TierBadge tier={m.tier} size="lg" /> : <span />}
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-wider text-white/45 font-mono">
            {m.expiry_date ? 'Expires' : 'Lifetime'}
          </div>
          <div className="text-xs text-white/85 mt-0.5">{expiryLabel(m)}</div>
        </div>
      </div>

      {(m.benefits ?? []).length > 0 ? (
        <div
          className="mt-4 pt-4 border-t border-white/8 relative"
          data-testid={`customer-profile-membership-benefits-${m.id}`}
        >
          <div className="text-[10px] uppercase tracking-wider text-gold/80 font-semibold mb-2">
            Benefits
          </div>
          <ul className="space-y-1.5">
            {m.benefits.slice(0, 4).map((b) => (
              <li key={b.id} className="text-xs text-white/80 flex gap-2">
                <span className="text-gold">•</span>
                <span className="truncate">{b.name}</span>
              </li>
            ))}
            {m.benefits.length > 4 ? (
              <li className="text-[11px] text-white/45 italic">
                +{m.benefits.length - 4} more
              </li>
            ) : null}
          </ul>
        </div>
      ) : null}

      {m.price_paid ? (
        <div className="mt-3 text-[10px] text-white/35 font-mono relative">
          Paid {rupiah(m.price_paid)} · since {formatDate(m.start_date)}
        </div>
      ) : null}
    </div>
  );
}
